/* ============================================
   WORK LINKS COMPONENT
   The Creative Orchestra - GEMINI Framework
   Action buttons for work pages
   ============================================ */

import { motion } from 'framer-motion';
import { 
  ExternalLink, 
  Download, 
  FileText, 
  Github, 
  Globe, 
  Play,
  BookOpen,
  Database,
  Cpu,
} from 'lucide-react';
import { useTheme } from '@context/ThemeContext';
import { LINK_TYPES } from '@utils/constants';

/**
 * Work Links Component
 * Displays action buttons for various work resources
 * 
 * @param {Object} props
 * @param {Object} props.links - Links object from work data
 * @param {string} props.workName - Work name for labels
 */
const WorkLinks = ({ links, workName = 'this work' }) => {
  const { activeTheme } = useTheme();
  const accentColor = activeTheme?.colors?.accent || '#818CF8';
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  if (!links) return null;

  // Filter out null/undefined links and group by type
  const availableLinks = Object.entries(links)
    .filter(([_, url]) => url && url !== '#')
    .map(([type, url]) => ({
      type,
      url,
      ...getLinkConfig(type),
    }));

  const primaryLinks = availableLinks.filter(l => l.primary);
  const secondaryLinks = availableLinks.filter(l => !l.primary);

  if (availableLinks.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 text-sm">
          Links coming soon...
        </p>
      </div>
    );
  }

  return (
    <section className="py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h3 className="text-xl font-semibold text-white mb-8 text-center">
          Explore {workName}
        </h3>

        {/* Primary links (larger buttons) */}
        {primaryLinks.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            {primaryLinks.map((link, index) => (
              <PrimaryLinkButton
                key={link.type}
                link={link}
                index={index}
                accentColor={accentColor}
                accentRgb={accentRgb}
              />
            ))}
          </div>
        )}

        {/* Secondary links (smaller buttons) */}
        {secondaryLinks.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3">
            {secondaryLinks.map((link, index) => (
              <SecondaryLinkButton
                key={link.type}
                link={link}
                index={index}
                accentColor={accentColor}
                accentRgb={accentRgb}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Get link configuration based on type
 */
function getLinkConfig(type) {
  const configs = {
    website: {
      label: 'Visit Website',
      icon: Globe,
      primary: true,
    },
    demo: {
      label: 'Try Demo',
      icon: Play,
      primary: true,
    },
    paper: {
      label: 'Read Paper',
      icon: FileText,
      primary: true,
    },
    documentation: {
      label: 'Documentation',
      icon: BookOpen,
      primary: false,
    },
    github: {
      label: 'View Code',
      icon: Github,
      primary: false,
    },
    download: {
      label: 'Download',
      icon: Download,
      primary: true,
    },
    userGuide: {
      label: 'User Guide',
      icon: BookOpen,
      primary: false,
    },
    model: {
      label: 'AI Model',
      icon: Cpu,
      primary: false,
    },
    dataset: {
      label: 'Dataset',
      icon: Database,
      primary: false,
    },
  };

  return configs[type] || {
    label: type.charAt(0).toUpperCase() + type.slice(1),
    icon: ExternalLink,
    primary: false,
  };
}

/**
 * Primary Link Button (larger, more prominent)
 */
const PrimaryLinkButton = ({ link, index, accentColor, accentRgb }) => {
  const Icon = link.icon;
  const isExternal = link.url.startsWith('http');
  const isPdf = link.url.endsWith('.pdf');

  return (
    <motion.a
      href={link.url}
      target={isExternal || isPdf ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className="inline-flex items-center gap-3 px-6 py-3 rounded-xl
        font-medium transition-all duration-300 group"
      style={{
        background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
        boxShadow: `0 0 20px rgba(${accentRgb}, 0.3)`,
      }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ 
        scale: 1.02,
        boxShadow: `0 0 30px rgba(${accentRgb}, 0.5)`,
      }}
      whileTap={{ scale: 0.98 }}
    >
      <Icon className="w-5 h-5 text-white" />
      <span className="text-white">{link.label}</span>
      {isExternal && (
        <ExternalLink className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" />
      )}
    </motion.a>
  );
};

/**
 * Secondary Link Button (smaller, outlined)
 */
const SecondaryLinkButton = ({ link, index, accentColor, accentRgb }) => {
  const Icon = link.icon;
  const isExternal = link.url.startsWith('http');
  const isPdf = link.url.endsWith('.pdf');

  return (
    <motion.a
      href={link.url}
      target={isExternal || isPdf ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg
        text-sm font-medium transition-all duration-300 group
        border"
      style={{
        background: `rgba(${accentRgb}, 0.05)`,
        borderColor: `rgba(${accentRgb}, 0.2)`,
        color: accentColor,
      }}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.2 + index * 0.05 }}
      whileHover={{ 
        background: `rgba(${accentRgb}, 0.15)`,
        borderColor: `rgba(${accentRgb}, 0.4)`,
      }}
      whileTap={{ scale: 0.98 }}
    >
      <Icon className="w-4 h-4" />
      <span>{link.label}</span>
      {isExternal && (
        <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
      )}
    </motion.a>
  );
};

/**
 * Compact Links Row (for use in cards or headers)
 */
export const CompactLinks = ({ links, className = '' }) => {
  if (!links) return null;

  const availableLinks = Object.entries(links)
    .filter(([_, url]) => url && url !== '#')
    .slice(0, 4); // Show max 4 links

  if (availableLinks.length === 0) return null;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {availableLinks.map(([type, url]) => {
        const config = getLinkConfig(type);
        const Icon = config.icon;
        const isExternal = url.startsWith('http');

        return (
          <a
            key={type}
            href={url}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            className="p-2 rounded-lg bg-white/5 text-gray-400 
              hover:bg-white/10 hover:text-white transition-colors"
            title={config.label}
          >
            <Icon className="w-4 h-4" />
          </a>
        );
      })}
    </div>
  );
};

/**
 * Link Card (for displaying a single prominent link)
 */
export const LinkCard = ({ 
  type, 
  url, 
  description,
  accentColor = '#818CF8',
  accentRgb = '129, 140, 248',
}) => {
  if (!url || url === '#') return null;

  const config = getLinkConfig(type);
  const Icon = config.icon;
  const isExternal = url.startsWith('http');

  return (
    <motion.a
      href={url}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className="block p-6 rounded-xl border transition-all duration-300 group"
      style={{
        background: `rgba(${accentRgb}, 0.05)`,
        borderColor: `rgba(${accentRgb}, 0.1)`,
      }}
      whileHover={{
        y: -4,
        background: `rgba(${accentRgb}, 0.1)`,
        borderColor: `rgba(${accentRgb}, 0.3)`,
      }}
    >
      <div className="flex items-start gap-4">
        <div 
          className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: `rgba(${accentRgb}, 0.2)` }}
        >
          <Icon className="w-6 h-6" style={{ color: accentColor }} />
        </div>
        <div className="flex-grow">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-white group-hover:text-indigo-300 transition-colors">
              {config.label}
            </h4>
            {isExternal && (
              <ExternalLink className="w-4 h-4 text-gray-500" />
            )}
          </div>
          {description && (
            <p className="text-sm text-gray-400">{description}</p>
          )}
        </div>
      </div>
    </motion.a>
  );
};

export default WorkLinks;
